import React from 'react';

import { type FormatDateOptions, FormattedDate, FormattedMessage } from 'react-intl-next';

import { type Activity } from '../../models/ActivityObject';
import { type WorkItemProps } from '../WorkItem';
import WorkList from '../WorkList';
import { type WorkListProps } from '../WorkList/WorkList';

import { getI18nDataForManyEntries, getI18nDataForSingleEntry } from './helper';
import { messages } from './messages';

const timeFormatOptions: FormatDateOptions = {
	month: 'long',
	day: 'numeric',
	year: 'numeric',
};

type Props = {
	isInitiallyExpanded?: boolean;
	currentUserId?: string;
	userId?: string;
	titleText?: string;
	activities?: Activity[];
} & Pick<
	WorkListProps,
	| 'onClickViewAllLink'
	| 'onClickShowMore'
	| 'onClickActivityEntry'
	| 'isLoading'
	| 'limitDefault'
	| 'limitExpanded'
	| 'emptyView'
	| 'viewAllLink'
>;

const getDescription = (activityObject: Activity, currentUserId: string) => {
	if (activityObject.activity.length <= 0 || !activityObject.activity[0].user) {
		return null;
	}

	const description =
		activityObject.activity.length > 1
			? getI18nDataForManyEntries(activityObject.activity, currentUserId)
			: getI18nDataForSingleEntry(activityObject.activity[0], currentUserId);

	return description;
};

const isDefined = (prop: WorkItemProps | null) => {
	return !!prop;
};

export function convertActivityToWorkItem(
	activities: Activity[],
	currentUserId: string,
): WorkItemProps[] {
	return activities
		.map((activityObject) => {
			const i18nForDescription = getDescription(activityObject, currentUserId);

			if (!i18nForDescription) {
				return null;
			}

			if (
				i18nForDescription &&
				'time' in i18nForDescription.params &&
				typeof i18nForDescription.params.time === 'string'
			) {
				i18nForDescription.params.time = (
					<FormattedDate {...timeFormatOptions} value={i18nForDescription.params.time} />
				);
			}

			const {
				name = '',
				iconUrl = '',
				url = '',
				container = '',
				provider = '',
			} = activityObject?.object || {};

			return {
				objectName: name,
				objectIconUrl: iconUrl,
				objectUrl: url,
				containerName: container,
				provider,
				description: i18nForDescription ? (
					<FormattedMessage {...i18nForDescription.key} values={i18nForDescription.params} />
				) : (
					''
				),
			};
		})
		.filter(isDefined) as WorkItemProps[];
}

const getInfoText = (userId?: string, currentUserId?: string) => {
	/**
	 * Return null if:
	 *  - userId or currentUserId is not defined
	 *  - current user is not viewing own activity
	 */
	if (!userId || !currentUserId || userId !== currentUserId) {
		return null;
	}

	return <FormattedMessage {...messages.privacyAssuringInfoText} />;
};

export default function WorkActivityList(props: Props) {
	const { activities, currentUserId, userId, titleText, emptyView, isLoading, ...otherProps } =
		props;

	const workItems = convertActivityToWorkItem(activities || [], currentUserId!);

	return (
		<WorkList
			{...otherProps}
			workItems={workItems}
			isInitiallyExpanded={false}
			emptyView={emptyView}
			titleText={titleText}
			infoText={getInfoText(userId, currentUserId)}
			isLoading={isLoading}
		/>
	);
}
